'use client'

import { useEffect, useRef, useState } from 'react'

function applicationServerKey(value) {
  const padding = '='.repeat((4 - value.length % 4) % 4)
  const raw = atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, character => character.charCodeAt(0))
}

export default function MessengerClient({ email, widgetRevision, previewAuthenticated = false }) {
  const host = useRef(null)
  const [pushState, setPushState] = useState('checking')
  const [isIos, setIsIos] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [pushInviteDismissed, setPushInviteDismissed] = useState(false)
  useEffect(() => {
    setPushInviteDismissed(window.localStorage.getItem('bvg-push-invite-dismissed') === '1')
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
    const installed = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true
    setIsIos(ios); setIsInstalled(installed)
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return setPushState('unsupported')
    navigator.serviceWorker.register('/push-service-worker.js').then(async registration => {
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        const response = await fetch('/api/push/subscriptions', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ subscription }) })
        if (!response.ok) throw new Error('subscription was not restored')
      }
      setPushState(subscription ? 'enabled' : Notification.permission === 'denied' ? 'denied' : 'available')
    }).catch(() => setPushState('unavailable'))
  }, [])
  const dismissPushInvite = () => {
    window.localStorage.setItem('bvg-push-invite-dismissed', '1')
    setPushInviteDismissed(true)
  }
  const enablePush = async () => {
    try {
      setPushState('enabling')
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return setPushState(permission === 'denied' ? 'denied' : 'available')
      const configResponse = await fetch('/api/push/subscriptions', { cache: 'no-store' })
      if (!configResponse.ok) throw new Error('Push configuration unavailable')
      const { public_key: publicKey } = await configResponse.json()
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey(publicKey) })
      const response = await fetch('/api/push/subscriptions', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ subscription }) })
      if (!response.ok) throw new Error('Subscription was not saved')
      setPushState('enabled')
    } catch (error) {
      setPushState(Notification.permission === 'denied' ? 'denied' : 'unavailable')
    }
  }
  useEffect(() => {
    let cancelled = false
    const openNotificationTarget = event => {
      if (event.data?.type !== 'bvg-notification-open') return
      let target
      try { target = new URL(event.data.url, window.location.origin) } catch { return }
      if (target.origin !== window.location.origin) return
      const conversationId = target.searchParams.get('conversation')
      const messageId = target.searchParams.get('message')
      if (!conversationId) return
      window.history.replaceState({}, '', `${target.pathname}${target.search}${target.hash}`)
      const messenger = host.current?.querySelector('bvg-messenger')
      if (!messenger) return
      messenger.setAttribute('initial-conversation-id', conversationId)
      if (messageId) messenger.setAttribute('initial-message-id', messageId)
      void messenger.openDeepLink?.(conversationId, messageId)
    }
    navigator.serviceWorker?.addEventListener('message', openNotificationTarget)
    const script = document.createElement('script')
    const revision = encodeURIComponent(widgetRevision || 'local')
    script.type = 'module'; script.src = `/widget/v1/bvg-messenger.js?rev=${revision}`
    script.onload = () => {
      if (cancelled || !host.current || host.current.firstChild) return
      const messenger = document.createElement('bvg-messenger')
      const initialConversation = new URLSearchParams(window.location.search).get('conversation')
      const initialMessage = new URLSearchParams(window.location.search).get('message')
      const initialAction = new URLSearchParams(window.location.search).get('action')
      for (const [name, value] of Object.entries({ 'api-base': '/api', 'service-root': '', 'widget-version': 'v1', 'widget-revision': widgetRevision || 'local', 'operator-email': email, 'settings-path': '/settings', ...(initialConversation ? { 'initial-conversation-id': initialConversation } : {}), ...(initialMessage ? { 'initial-message-id': initialMessage } : {}), ...(initialAction ? { 'initial-action': initialAction } : {}) })) messenger.setAttribute(name, value)
      messenger.setAttribute('full-page', '')
      let reauthenticating = false
      messenger.addEventListener('bvg-auth-required', () => {
        if (reauthenticating) return
        reauthenticating = true
        if (previewAuthenticated) {
          window.location.assign('/api/preview-auth/signin')
          return
        }
        const callbackUrl = window.location.pathname + window.location.search
        window.location.assign(`/api/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`)
      })
      messenger.addEventListener('bvg-sign-out', () => window.location.assign('/api/auth/signout'))
      messenger.addEventListener('bvg-switch-user', () => window.location.assign('/api/auth/signout?callbackUrl=/api/auth/signin'))
      host.current.append(messenger)
    }
    document.head.append(script)
    return () => { cancelled = true; script.remove(); navigator.serviceWorker?.removeEventListener('message', openNotificationTarget) }
  }, [email, previewAuthenticated, widgetRevision])
  return <>
    {pushState === 'available' && !pushInviteDismissed && (!isIos || isInstalled) && <aside className="push-invite" aria-label="Notification setup"><div><strong>Know when an agent replies</strong><span>Enable private, native notifications on this device.</span></div><button type="button" onClick={enablePush}>Enable notifications</button><button type="button" onClick={dismissPushInvite} aria-label="Dismiss notification setup">Dismiss</button></aside>}
    {pushState === 'available' && isIos && !isInstalled && <aside className="push-invite" aria-label="Install Messenger"><div><strong>Get notifications on iPhone</strong><span>In Safari, tap Share, then Add to Home Screen. Open Messenger from its new icon to enable notifications.</span></div></aside>}
    {pushState === 'denied' && <aside className="push-invite push-warning"><div><strong>Notifications are blocked</strong><span>Enable Messenger in iPhone Settings → Notifications, or in this browser's site settings.</span></div></aside>}
    <div ref={host} className="messenger-host" aria-label="BVG Messenger" />
  </>
}
